import { Injectable } from "@angular/core";
import { ApiUrlService } from "./api-url.service";
import { CacheService } from "./cache.service";
import { NetworkService } from "./network.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { catchError, EMPTY, map, of, take, tap } from "rxjs";
import { lastValueFrom } from "rxjs";
import { Capacitor } from "@capacitor/core";

@Injectable({
  providedIn: "root",
})
export class FeatureService {
  private apiUrl = `${this.apiUrlService.getApiUrl()}/api/features`;
  private apiUrlOrder = `${this.apiUrlService.getApiUrl()}/api/features/order`;
  private featureDescriptions: any[] = [];
  private readonly isNative = Capacitor.getPlatform() !== "web";
  private readonly featureCacheKey = "features-list"; // <‑‑ single cached object key
  private readonly homeSnapshotKey = "home-feature-snapshot";
  private homeSnapshot: { owner: string; features: any[] } | null = null;

  constructor(
    private http: HttpClient,
    private networkService: NetworkService,
    private apiUrlService: ApiUrlService,
    private cacheService: CacheService
  ) {}

  /**
   * Clear in-memory feature descriptions cache.
   * Call this on logout to ensure fresh features are loaded on next login.
   */
  public clearFeatureDescriptionsCache(): void {
    console.log("FeatureService clearing in-memory feature descriptions cache");
    this.featureDescriptions = [];
    this.clearHomeSnapshot();
  }

  /**
   * Last fully-ordered home screen list, so the home screen can render
   * instantly (in-memory for in-app navigation, localStorage for cold starts)
   * and refresh in the background instead of showing the loading skeleton
   * on every visit. `owner` is "<user_id>:<org_id>" so a snapshot is never
   * shown to a different user/org.
   */
  public getHomeSnapshot(): { owner: string; features: any[] } | null {
    if (this.homeSnapshot) return this.homeSnapshot;
    try {
      const raw = localStorage.getItem(this.homeSnapshotKey);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed?.owner && Array.isArray(parsed.features)) {
        this.homeSnapshot = parsed;
      }
    } catch (e) {
      console.warn("FeatureService unable to read home snapshot", e);
    }
    return this.homeSnapshot;
  }

  public setHomeSnapshot(owner: string, features: any[]): void {
    this.homeSnapshot = { owner, features };
    try {
      localStorage.setItem(
        this.homeSnapshotKey,
        JSON.stringify(this.homeSnapshot)
      );
    } catch (e) {
      console.warn("FeatureService unable to persist home snapshot", e);
    }
  }

  public clearHomeSnapshot(): void {
    this.homeSnapshot = null;
    try {
      localStorage.removeItem(this.homeSnapshotKey);
    } catch (e) {
      // ignore - storage may be unavailable
    }
  }

  /**
   * Clear the persisted, org-agnostic "features-list" cache (native only -
   * web always fetches fresh, see getFeaturesWithTimeout above). This key
   * isn't scoped per-org and nothing else invalidates it, so after
   * switching orgs on a native device the previous org's feature list kept
   * being served indefinitely. Call this before an org/linked-account
   * switch's reload so the reload's fetch is genuinely fresh.
   */
  public async clearPersistedFeaturesCache(): Promise<void> {
    this.clearHomeSnapshot();
    if (!this.isNative) return;
    console.log("FeatureService clearing persisted features-list cache");
    await this.cacheService.removeCachedData(this.featureCacheKey);
  }

  /**
   * Retrieve available features with a two‑tier strategy:
   *
   * ‣ **Capacitor**
   *    1. Attempt to load the JSON array from IndexedDB/Cache (via CacheService).
   *    2. If present ⇒ return immediately **and** refresh the cache in the background.
   *    3. If absent ⇒ fetch from API, then write the response to cache.
   *
   * ‣ **Web (PWA / browser)** – always pull fresh from API (legacy behaviour).
   */
  public async getFeaturesWithTimeout(): Promise<any[] | null> {
    console.log("FeatureService getFeaturesWithTimeout");
    const cacheKey = this.featureCacheKey;

    /* ───────────────────────── Capacitor branch ─────────────────────── */
    if (this.isNative) {
      try {
        const cached = await this.cacheService.getCachedData(cacheKey);
        if (cached) {
          console.log("FeatureService returning cached feature list");

          // Background refresh disabled to prevent cache confusion during upgrades
          // this.refreshFeaturesInBackground(cacheKey);
          return cached;
        }
      } catch (e) {
        console.warn(
          "FeatureService cache lookup failed – proceeding to network",
          e
        );
      }
    }

    /* ───────────────────────── Network fetch (also web default) ─────── */
    try {
      const fresh = await this.http
        .get<any[]>(this.apiUrl, {
          headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          params: new HttpParams(),
        })
        .toPromise();

      // Persist to cache for native use‑cases
      if (this.isNative && fresh) {
        try {
          await this.cacheService.setCachedData(cacheKey, fresh);
        } catch (e) {
          console.warn(
            "FeatureService unable to write feature list to cache",
            e
          );
        }
      }

      return fresh;
    } catch (error: any) {
      console.error("FeatureService error fetching features:", error);
      if (this.networkService.isNetworkError(error)) {
        this.networkService.markInternetDown();
      }
      return null;
    }
  }

  /** Background updater (native only) */
  private refreshFeaturesInBackground(cacheKey: string): void {
    console.log("FeatureService refreshing features in background");
    this.http
      .get<any[]>(this.apiUrl, {
        headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
        params: new HttpParams(),
      })
      .toPromise()
      .then(async (fresh) => {
        if (fresh) {
          await this.cacheService.setCachedData(cacheKey, fresh);
          console.log("FeatureService feature list cache refreshed");
        }
      })
      .catch((err) =>
        console.warn("FeatureService background feature refresh failed", err)
      );
  }

  /* ───────────────────────── rest of class unchanged ────────────────── */

  // Main entry‑point: download (or load from cache) the description JSONs
  public async getFeatureDescriptions(
    onLoadingStateChange?: (isLoading: boolean) => void
  ): Promise<any[]> {
    const features = await this.getFeaturesWithTimeout();

    if (!features?.length) {
      console.log("No features available");
      return [];
    }

    // Try to load whatever is already cached first
    let cached = await this.loadCachedFeatureDescriptions();

    console.log("Features from API:", features.length);
    console.log(
      "Cached feature descriptions (all orgs ever cached):",
      cached.length
    );

    // `cached` is a union across every org this device has ever visited
    // (that's the point of the offline cache), so its raw length isn't a
    // meaningful measure of "do we have THIS org's features cached" - a
    // device that's visited 3 orgs with 5 features each has 15 cached
    // descriptions, which would satisfy hasFullCache for a brand new
    // org with 2 features that were never actually downloaded. Count how
    // many of the CURRENT org's features actually have a cached match
    // instead.
    const countMatchingFeatures = (cachedArr: any[], featuresArr: any[]) => {
      const cachedUrls = new Set(cachedArr.map((c) => c.baseUrl?.trim()));
      return featuresArr.filter((f) => cachedUrls.has(f.feature_url?.trim()))
        .length;
    };

    let matchedCount = countMatchingFeatures(cached, features);
    console.log(
      `Of this org's ${features.length} features, ${matchedCount} are cached`
    );

    // Log which features are missing from cache
    if (matchedCount < features.length) {
      const cachedUrls = new Set(cached.map((c) => c.baseUrl?.trim()));
      const missingFeatures = features.filter(
        (f) => !cachedUrls.has(f.feature_url?.trim())
      );
      console.log(
        "Missing from cache:",
        missingFeatures.map((f) => f.feature_url || f.baseUrl)
      );
    }

    // Determine if we have a full cache or at least 90% cached (good enough to show immediately)
    const hasFullCache = matchedCount > 0 && matchedCount >= features.length;
    const hasMostlyCached =
      matchedCount > 0 && matchedCount >= features.length * 0.9;
    console.log("Has full cache:", hasFullCache);
    console.log("Has mostly cached (90%+):", hasMostlyCached);

    // Prepare & start background fetches
    let fetchPromises: Promise<any>[] = [];
    fetchPromises = features.map(async (feature) => {
      const featureUrl = feature.feature_url
        ? `${feature.feature_url}feature-description.json`
        : `${feature.baseUrl}feature-description.json`;

      try {
        const description = await lastValueFrom(
          this.cacheService.getResource(featureUrl, true)
        );

        console.log(
          `Fetched feature description for ${featureUrl}:`,
          description?.data?.homeName
        );

        // Cache additional resources if needed
        const shouldCache = description?.data?.offline === "true";
        if (this.isNative || shouldCache) {
          // Fire-and-forget for additional resources
          this.cacheFeatureResources(description.data).catch((err) => {
            console.error(`Failed to cache resources for ${featureUrl}`, err);
          });
        }

        return description;
      } catch (err) {
        console.error(`Failed to fetch ${featureUrl}`, err);
        return null;
      }
    });

    // If we have most features cached (90%+), return them immediately without showing loading
    // Only show loading indicator if we have very few or no cached features
    if (!hasMostlyCached) {
      console.log(
        `Insufficient cache (${matchedCount}/${features.length} of this org's features). Waiting for feature descriptions to download...`
      );

      // Notify that we're loading from the web
      if (onLoadingStateChange) {
        console.log("Invoking onLoadingStateChange(true)");
        onLoadingStateChange(true);
      }

      const results = await Promise.all(fetchPromises);
      console.log(
        `Downloaded ${
          results.filter((r) => r !== null).length
        } feature descriptions`
      );

      // Reload from cache after all fetches complete
      // The getResource observable should have completed the cache writes by now
      // Add a small delay to ensure filesystem writes are flushed
      await new Promise((resolve) => setTimeout(resolve, 100));
      cached = await this.loadCachedFeatureDescriptions();
      matchedCount = countMatchingFeatures(cached, features);
      console.log(
        `After waiting, cached feature descriptions: ${cached.length} total, ${matchedCount} matching this org`
      );

      // If still missing some, retry with exponential backoff and force refresh
      let retries = 0;
      const maxRetries = 3;
      console.log(
        `Entering retry loop check: matched=${matchedCount}, expected=${features.length}`
      );
      while (matchedCount < features.length && retries < maxRetries) {
        retries++;
        const delay = 100 * Math.pow(2, retries); // 200ms, 400ms, 800ms
        console.warn(
          `Cache mismatch: expected ${features.length}, got ${matchedCount}. Retry ${retries}/${maxRetries} after ${delay}ms...`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        cached = await this.loadCachedFeatureDescriptions(true); // Force refresh on retry
        matchedCount = countMatchingFeatures(cached, features);
        console.log(
          `After retry ${retries}, cached feature descriptions: ${cached.length} total, ${matchedCount} matching this org`
        );

        // Log which features are still missing after retry
        if (matchedCount < features.length) {
          const cachedUrls = new Set(cached.map((c) => c.baseUrl?.trim()));
          const stillMissing = features.filter(
            (f) => !cachedUrls.has(f.feature_url?.trim())
          );
          console.log(
            `Still missing after retry ${retries}:`,
            stillMissing.map((f) => f.feature_url || f.baseUrl)
          );
        }
      }

      // If cache still empty after retries, use the downloaded results directly
      console.log(
        `After retry loop: cached=${cached.length}, results=${results.length}`
      );
      if (cached.length === 0 && results.length > 0) {
        console.warn(
          `Cache failed to populate after ${maxRetries} retries. Using downloaded results directly.`
        );
        console.log(`Sample result structure:`, results[0]);
        cached = results
          .filter((r) => r !== null)
          .map((r) => r?.data)
          .filter((d) => d !== null && d !== undefined);
        console.log(
          `Using ${cached.length} downloaded feature descriptions directly from results`
        );
      } else if (cached.length === 0) {
        console.error(
          `No cached features and no downloaded results available!`
        );
      }

      // Notify that loading is complete
      if (onLoadingStateChange) {
        onLoadingStateChange(false);
      }
    } else {
      console.log(
        `Using cached feature descriptions (${cached.length}/${features.length}).`
      );
      // Background fetching disabled to prevent cache confusion during upgrades
      // void Promise.all(fetchPromises);
    }

    // Merge in feature_id, then drop anything not in the CURRENT org's
    // authorized feature list. `cached` holds every feature-description.json
    // ever downloaded to this device across every org the user has ever
    // been in (that's the point of the offline cache) - without this
    // filter, switching orgs was a straight union: org A's cached features
    // never went away when switching to org B, and vice versa.
    const withFeatureId = cached
      .map((fdata) => {
        const featureId = features.find(
          (feat) => feat.feature_url?.trim() === fdata.baseUrl?.trim()
        )?.feature_id;

        if (!featureId) {
          console.warn(`Could not find feature_id for ${fdata.baseUrl}`);
        }

        return { ...fdata, feature_id: featureId };
      })
      .filter((fdata) => !!fdata.feature_id);

    console.log(`Returning ${withFeatureId.length} features with IDs`);
    this.featureDescriptions = withFeatureId;

    // Fire-and-forget: keep each feature's contributed permission verbs
    // (declared under "permissionVerbs" in its feature-description.json) in
    // sync with the backend, so the Roles admin screen can offer them when
    // building a role. Non-blocking - doesn't hold up rendering the home
    // screen, and the backend call is idempotent (a $set replace), so this
    // running on every load is intentional and cheap.
    this.syncFeaturePermissionVerbs(withFeatureId);

    return withFeatureId;
  }

  /**
   * Push each feature's declared `permissionVerbs` up to the backend so it
   * stays the source of truth for what a role can be granted. Uses a wholesale
   * replace server-side, so when a new version of a feature's description
   * drops a verb or adds a different one, the DB ends up matching exactly -
   * no stale verbs left behind, no duplicates from repeated syncs.
   */
  private syncFeaturePermissionVerbs(features: any[]): void {
    for (const feature of features) {
      const featureId = feature?.feature_id;
      const permissionVerbs = feature?.permissionVerbs;
      if (!featureId || !Array.isArray(permissionVerbs)) {
        continue;
      }

      const verbs = permissionVerbs.map((v: any) => ({
        verb: v.verb,
        label: v.label,
        description: v.description ?? null,
      }));

      this.http
        .put(
          `${this.apiUrlService.getApiUrl()}/api/features/${featureId}/verbs`,
          { verbs }
        )
        .toPromise()
        .catch((err) => {
          console.warn(
            `FeatureService: failed to sync permission verbs for ${featureId}`,
            err
          );
        });
    }
  }

  // Cache the auxiliary resources for a given feature
  private async cacheFeatureResources(feature: any): Promise<void> {
    console.log("FeatureService caching resources for feature:", feature);
    const baseUrl = feature.baseUrl || feature.feature_url;
    const resourcesToCache = [
      "feature-description.json",
      "index.html",
      "bundle.js",
      feature.homeIcon,
      ...(feature.footerIcons?.flatMap((icon: any) => [
        icon.iconSrc,
        icon.negIconSrc,
      ]) || []),
    ];

    for (const resource of resourcesToCache) {
      const fullUrl = `${baseUrl}${resource}`;
      try {
        await lastValueFrom(
          this.cacheService.getResourceWithoutRefresh(fullUrl, true)
        );
      } catch (err) {
        console.error("Error caching resource:", fullUrl, err);
      }
    }
  }

  // Retrieve any cached feature‑descriptions from storage
  private async loadCachedFeatureDescriptions(
    forceRefresh = false
  ): Promise<any[]> {
    console.log(
      "FeatureService loadCachedFeatureDescriptions - START (forceRefresh: " +
        forceRefresh +
        ")"
    );
    const items = await this.cacheService.getCachedItems(
      (key) => key.endsWith("feature-description.json"),
      forceRefresh
    );
    console.log(`FeatureService found ${items.length} cached items`);

    const cachedDescriptions = items.map((item) => {
      console.log(
        "FeatureService cached item:",
        item?.data?.homeName || "unknown"
      );
      return item.data;
    });

    console.log(
      `FeatureService loadCachedFeatureDescriptions - DONE: ${cachedDescriptions.length} descriptions`
    );
    return cachedDescriptions;
  }

  /* ───────────────────────── helper look‑ups (unchanged) ────────────── */

  public getBaseUrlByBasePath(basePath: string): Promise<any> {
    return this.featureDescriptions.length === 0
      ? this.getFeatureDescriptions().then(() =>
          this.findFeatureUrlByPath(basePath)
        )
      : Promise.resolve(this.findFeatureUrlByPath(basePath));
  }

  private findFeatureUrlByPath(basePath: string) {
    return this.featureDescriptions.find(
      (f) => f.defaultPath.replace(/\//g, "") === basePath
    )?.feature_url;
  }

  public async getFeatureDescriptionByBasePath(basePath: string): Promise<any> {
    console.log("React Native getFeatureDescriptionByBasePath - START");
    console.log("React Native basePath param:", basePath);

    console.log("React Native calling getFeatureDescriptions()...");
    const startTime = Date.now();
    const features = await this.getFeatureDescriptions();
    const duration = Date.now() - startTime;

    console.log(
      `React Native getFeatureDescriptions() completed in ${duration}ms`
    );
    console.log("React Native features count:", features?.length);
    console.log("React Native searching for basePath:", basePath);

    const result = features.find(
      (f) => f.defaultPath.replace(/\//g, "") === basePath
    );

    console.log("React Native found feature:", result?.homeName || "NOT FOUND");
    console.log("React Native getFeatureDescriptionByBasePath - RETURNING");
    return result;
  }

  public async getFeatureDescriptionByBaseUrl(baseUrl: string): Promise<any> {
    if (this.featureDescriptions.length === 0)
      await this.getFeatureDescriptions();
    const trimmedBaseUrl = baseUrl.trim();
    return this.featureDescriptions.find(
      (f) => (f.feature_url ?? f.baseUrl)?.trim() === trimmedBaseUrl
    );
  }

  public async getFeatureOrder(orgId: string): Promise<any | null> {
    const startTime = performance.now();
    console.log("[FEATURE-ORDER-PERF] getFeatureOrder START for org:", orgId);
    const cacheKey = `featureOrder:${orgId}`;
    try {
      const cacheStart = performance.now();
      let cachedData = await this.cacheService.getCachedData(cacheKey);
      const cacheDuration = performance.now() - cacheStart;
      console.log(
        `[FEATURE-ORDER-PERF] Cache lookup took ${cacheDuration.toFixed(2)}ms`
      );

      if (cachedData) {
        try {
          console.log(
            "FeatureService getFeatureOrder: cache hit: ",
            cachedData
          );
          const order = cachedData;
          console.log(
            "FeatureService getFeatureOrder: parsed cache hit: ",
            order
          );

          // Background refresh disabled to prevent cache confusion during upgrades
          // this.http
          //   .get<any[]>(`${this.apiUrlOrder}/${orgId}`, {
          //     headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          //     params: new HttpParams(),
          //   })
          //   .pipe(
          //     take(1), // single hit
          //     tap((fresh) => this.cacheService.setCachedData(cacheKey, fresh)),
          //     catchError((err) => {
          //       console.error(
          //         `FeatureService background refresh failed for org ${orgId}:`,
          //         err
          //       );
          //       return EMPTY; // swallow error
          //     })
          //   )
          //   .subscribe(); // no blocking

          const totalDuration = performance.now() - startTime;
          console.log(
            `[FEATURE-ORDER-PERF] getFeatureOrder TOTAL (cache hit) took ${totalDuration.toFixed(
              2
            )}ms`
          );
          return order;
        } catch (err) {
          console.error(
            `FeatureService error parsing cached order for org ${orgId}:`,
            err
          );
          // await this.cacheService.removeItem(cacheKey);
          return await this.fetchAndCacheFeatureOrder(cacheKey, orgId);
        }
      } else {
        console.log("[FEATURE-ORDER-PERF] No cache, fetching from server...");
        const result = await this.fetchAndCacheFeatureOrder(cacheKey, orgId);
        const totalDuration = performance.now() - startTime;
        console.log(
          `[FEATURE-ORDER-PERF] getFeatureOrder TOTAL (no cache) took ${totalDuration.toFixed(
            2
          )}ms`
        );
        return result;
      }
    } catch (err) {
      console.error("FeatureService error accessing cache:", err);
      const result = await this.fetchAndCacheFeatureOrder(cacheKey, orgId);
      const totalDuration = performance.now() - startTime;
      console.log(
        `[FEATURE-ORDER-PERF] getFeatureOrder TOTAL (error path) took ${totalDuration.toFixed(
          2
        )}ms`
      );
      return result;
    }
  }

  private async fetchAndCacheFeatureOrder(
    cacheKey: string,
    orgId: string
  ): Promise<any> {
    console.log("FeatureService fetchAndCacheFeatureOrder: ", cacheKey, orgId);

    // 1) Try to read from cache
    const cached = await this.cacheService.getCachedData(cacheKey);
    if (cached != null) {
      // Kick off a non-blocking refresh
      lastValueFrom(
        this.http.get<any[]>(`${this.apiUrlOrder}/${orgId}`, {
          headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          params: new HttpParams(),
        })
      )
        .then((orderData) => {
          this.cacheService.setCachedData(cacheKey, orderData);
        })
        .catch((err) => {
          console.error("Background refresh failed:", err);
          if (this.networkService.isNetworkError(err)) {
            this.networkService.markInternetDown();
          }
        });

      // Immediately return stale (or fresh) cache
      return cached;
    }

    // 2) No cache yet — block until we get data (or return 404 indicator)
    try {
      const orderData = await lastValueFrom(
        this.http.get<any[]>(`${this.apiUrlOrder}/${orgId}`, {
          headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
          params: new HttpParams(),
        })
      );

      // Handle null response - don't cache, throw error to trigger retry
      if (orderData === null || orderData === undefined) {
        console.warn(
          "API returned null - not caching, treating as transient error"
        );
        throw new Error("API returned null - no feature order data");
      }

      // Cache valid response
      this.cacheService.setCachedData(cacheKey, orderData);
      return orderData;
    } catch (err: any) {
      console.error("FeatureService fetching feature order:", err);

      // Check if this is a 404 (no order exists yet)
      if (err.status === 404) {
        console.log(
          "No feature order found (404) - this is expected for new orgs"
        );
        // Return a special object indicating no order exists
        const noOrderResult = { notFound: true, list: [] };
        await this.cacheService.setCachedData(cacheKey, noOrderResult);
        return noOrderResult;
      }

      // Check if this is an auth error (401, 403) - don't cache these
      if (err.status === 401 || err.status === 403) {
        console.warn(
          "Auth error fetching feature order - not caching, will retry"
        );
        // Don't cache auth errors - throw so caller knows to retry
        throw err;
      }

      // For other errors (network, server errors), don't cache - throw to retry
      console.warn(
        "Network or server error fetching feature order - not caching"
      );
      if (this.networkService.isNetworkError(err)) {
        this.networkService.markInternetDown();
      }
      throw err;
    }
  }
}
